// composables/gamification/constellation-render.ts
//
// Pure rendering helpers for the shared constellation renderer (M4C-26).
//
// The renderer is *data-driven*: it never embeds Orion/Lyra/Cygnus point
// coordinates. It takes the backend-compatible geometry (`stars_data` /
// `segments_data`, normalized 0–100 — see types/gamification.d.ts) plus an
// optional per-member progress overlay, and projects everything into a fixed
// square render frame so a card, a detail view and a celebration modal all draw
// from the same engine at different sizes.
//
// Nothing here is reactive or Vue-specific — these are deterministic functions,
// so they're trivial to reason about and to exercise from a demo/fixtures page.

import type {
  ApiConstellationSegment,
  ApiConstellationStar,
  ConstellationStarProgress,
} from '@/types/gamification';

/* ------------------------------------------------------------------ */
/* Presentation types                                                  */
/* ------------------------------------------------------------------ */

/**
 * Visual state the renderer draws a star in — a superset of the data-layer
 * {@link StarState} (`earned | current | locked`). The renderer additionally
 * derives `partial` (a `current` star with fragment progress to show) and
 * `ghost` (a star with no progress overlay, drawn as faint background).
 */
export type StarRenderState = 'earned' | 'current' | 'partial' | 'locked' | 'ghost';

/** Preset selecting size / label defaults for a given render context. */
export type ConstellationRenderVariant =
  | 'card' // compact overview preview (M4C-20)
  | 'detail' // large selected-constellation view (M4C-21)
  | 'explainer' // static "how it works" diagram (M4C-28)
  | 'celebration' // star-born modal emphasis (M4C-29)
  | 'row'; // tiny inline profile-row preview (M4C-34)

/** A star projected into the fixed render frame with its resolved visual state. */
export interface RenderStar {
  id: string;
  order: number;
  label: string;
  /** Projected centre in the fixed {@link FRAME}×{@link FRAME} viewBox. */
  cx: number;
  cy: number;
  state: StarRenderState;
  /** Fill fraction 0–1 for a `current`/`partial` star; 1 earned, 0 otherwise. */
  progress: number;
  /** Extra "just-unlocked" emphasis ring. */
  highlighted: boolean;
}

/** A segment projected into the render frame, with how "lit" it should appear. */
export interface RenderSegment {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** `lit`: both ends sit on the traced path (earned/current). `dim`: leads to a locked/background star. */
  level: 'lit' | 'dim';
}

export interface NormalizedConstellation {
  stars: RenderStar[];
  segments: RenderSegment[];
  /** Fixed square render frame, e.g. `"0 0 100 100"`. */
  viewBox: string;
  earnedCount: number;
  total: number;
}

export interface NormalizeOptions {
  /** Per-member star states (earned/current/locked) from the data layer. */
  progress?: ConstellationStarProgress[];
  /** Member's cumulative fragment balance — fills the `current` star partially. */
  currentFragments?: number;
  /** Explicit per-star-id state overrides; win over `progress` (static/teaching views). */
  states?: Partial<Record<string, StarRenderState>>;
  /** Star id to emphasise as just-unlocked. */
  highlightStarId?: string | null;
  /** viewBox units kept clear around the geometry for glyphs/labels/glow. */
  padding?: number;
}

/** Side length of the fixed square render frame, in viewBox units. */
export const FRAME = 100;
const DEFAULT_PADDING = 18;

/* ------------------------------------------------------------------ */
/* Geometry                                                            */
/* ------------------------------------------------------------------ */

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Project raw 0–100 star coordinates into the fixed render frame.
 *
 * Fits the geometry's bounding box into a centred, padded square with a single
 * uniform scale (aspect preserved, no distortion) so every constellation — wide
 * or tall, sparse or dense — fills the frame consistently. This is why a card
 * and a detail view stay visually stable across widths: the viewBox is fixed and
 * the fit is deterministic.
 */
function projectStars(
  stars: ApiConstellationStar[],
  padding: number,
): Map<string, { cx: number; cy: number }> {
  const map = new Map<string, { cx: number; cy: number }>();
  if (stars.length === 0) {
    return map;
  }

  const xs = stars.map((s) => s.x);
  const ys = stars.map((s) => s.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const width = maxX - minX;
  const height = maxY - minY;
  const span = Math.max(width, height, 1e-6); // guard single-point / collinear geometry
  const content = FRAME - padding * 2;
  const scale = content / span;
  const offsetX = padding + (content - width * scale) / 2;
  const offsetY = padding + (content - height * scale) / 2;

  for (const star of stars) {
    map.set(star.id, {
      cx: round(offsetX + (star.x - minX) * scale),
      cy: round(offsetY + (star.y - minY) * scale),
    });
  }
  return map;
}

/** Fraction (0–1) of the current star's fragment band the member has filled. */
function partialFraction(required: number, prevRequired: number, current?: number): number {
  if (current == null) {
    return 0;
  }
  const band = required - prevRequired;
  if (band <= 0) {
    return current >= required ? 1 : 0;
  }
  return clamp((current - prevRequired) / band, 0, 1);
}

/**
 * Resolve every star's render state. Precedence per star:
 * explicit `states[id]` › derived from `progress` › `ghost` (no overlay).
 * The `current` star becomes `partial` when a fragment balance is available.
 */
function resolveStates(
  stars: ApiConstellationStar[],
  opts: NormalizeOptions,
): Map<string, { state: StarRenderState; progress: number }> {
  const result = new Map<string, { state: StarRenderState; progress: number }>();
  const progressById = new Map<string, ConstellationStarProgress>();
  (opts.progress ?? []).forEach((p) => progressById.set(p.id, p));

  // Walk in earn order so the previous earned threshold is known for the
  // current star's partial fill (thresholds are cumulative).
  const ordered = [...stars].sort((a, b) => a.order - b.order);
  let prevThreshold = 0;

  for (const star of ordered) {
    const override = opts.states?.[star.id];
    const prog = progressById.get(star.id);
    let state: StarRenderState;
    let fraction = 0;

    if (override) {
      state = override;
      if (state === 'earned') {
        fraction = 1;
      } else if (state === 'current' || state === 'partial') {
        fraction = partialFraction(star.fragments_required, prevThreshold, opts.currentFragments);
      }
    } else if (prog) {
      if (prog.state === 'earned') {
        state = 'earned';
        fraction = 1;
      } else if (prog.state === 'current') {
        fraction = partialFraction(star.fragments_required, prevThreshold, opts.currentFragments);
        state = fraction > 0 ? 'partial' : 'current';
      } else {
        state = 'locked';
      }
    } else {
      state = 'ghost';
    }

    result.set(star.id, { state, progress: fraction });
    if (state === 'earned') {
      prevThreshold = star.fragments_required;
    }
  }
  return result;
}

/**
 * Resolve segments by star *id* (never by order/index) so the outline is stable
 * when ids are non-sequential or sparse. Segments referencing a missing star are
 * dropped rather than throwing. A segment is `lit` when both endpoints sit on the
 * traced path (earned or current); otherwise it's `dim` (it leads to a locked or
 * background star).
 */
function resolveSegments(
  segments: ApiConstellationSegment[],
  projected: Map<string, { cx: number; cy: number }>,
  states: Map<string, { state: StarRenderState; progress: number }>,
): RenderSegment[] {
  const out: RenderSegment[] = [];
  const onPath = (s: StarRenderState) => s === 'earned' || s === 'current' || s === 'partial';

  for (const seg of segments) {
    const from = projected.get(seg.from_star_id);
    const to = projected.get(seg.to_star_id);
    if (!from || !to) {
      continue; // dangling reference — skip, keep the rest stable
    }

    const fromState = states.get(seg.from_star_id)?.state ?? 'ghost';
    const toState = states.get(seg.to_star_id)?.state ?? 'ghost';

    out.push({
      id: seg.id,
      x1: from.cx,
      y1: from.cy,
      x2: to.cx,
      y2: to.cy,
      level: onPath(fromState) && onPath(toState) ? 'lit' : 'dim',
    });
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

/**
 * Project raw constellation geometry + an optional progress overlay into a
 * draw-ready model (fixed viewBox, resolved star states, lit segments).
 */
export function normalizeConstellation(
  stars: ApiConstellationStar[],
  segments: ApiConstellationSegment[] = [],
  opts: NormalizeOptions = {},
): NormalizedConstellation {
  const padding = opts.padding ?? DEFAULT_PADDING;
  const projected = projectStars(stars, padding);
  const stateMap = resolveStates(stars, opts);
  const highlightId = opts.highlightStarId ?? null;

  const renderStars: RenderStar[] = [...stars]
    .sort((a, b) => a.order - b.order)
    .map((star) => {
      const point = projected.get(star.id) ?? { cx: FRAME / 2, cy: FRAME / 2 };
      const resolved = stateMap.get(star.id) ?? { state: 'ghost' as StarRenderState, progress: 0 };
      return {
        id: star.id,
        order: star.order,
        label: star.label,
        cx: point.cx,
        cy: point.cy,
        state: resolved.state,
        progress: resolved.progress,
        highlighted: highlightId === star.id,
      };
    });

  const renderSegments = resolveSegments(segments, projected, stateMap);
  const earnedCount = renderStars.filter((s) => s.state === 'earned').length;

  return {
    stars: renderStars,
    segments: renderSegments,
    viewBox: `0 0 ${FRAME} ${FRAME}`,
    earnedCount,
    total: renderStars.length,
  };
}

/**
 * Build an SVG path for an N-pointed star centred at (cx, cy). With `spikes: 4`
 * and a small inner ratio this yields the sparkle glyph used across the
 * gamification design system (cf. GamiFragment).
 */
export function starPolygonPath(
  cx: number,
  cy: number,
  spikes: number,
  outerRadius: number,
  innerRadius: number,
): string {
  const step = Math.PI / spikes;
  let angle = -Math.PI / 2; // start at the top point
  let path = '';

  for (let i = 0; i < spikes * 2; i += 1) {
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const x = round(cx + Math.cos(angle) * radius);
    const y = round(cy + Math.sin(angle) * radius);
    path += i === 0 ? `M${x} ${y}` : `L${x} ${y}`;
    angle += step;
  }
  return `${path}Z`;
}

/** Human-readable description of a star's state, for accessible names/titles. */
export function starStateLabel(state: StarRenderState): string {
  switch (state) {
    case 'earned':
      return 'earned';
    case 'current':
    case 'partial':
      return 'in progress';
    case 'locked':
      return 'locked';
    default:
      return 'background';
  }
}
